/**
 * Client API per il modulo Interventi.
 */

import axios from 'axios'
import client from './client'
import type { Attivita, Intervento, InterventoDetail, InterventoListResponse, InterventoNota, MediaFile, Rendiconto, TipologiaIntervento } from '@/types'

export const attivitaApi = {
  lista: async (attive_only = true): Promise<Attivita[]> => {
    const res = await client.get('/attivita', { params: { attive_only } })
    return res.data
  },
  crea: async (nome: string, ordine = 0): Promise<Attivita> => {
    const res = await client.post('/attivita', { nome, ordine })
    return res.data
  },
  aggiorna: async (id: string, data: Partial<Attivita>): Promise<Attivita> => {
    const res = await client.patch(`/attivita/${id}`, data)
    return res.data
  },
}

export interface ListinoAccordoVoce {
  id: string
  descrizione: string
  unita_misura: string
  prezzo: number
  categoria: string
  codice_cer: string | null
  solo_se_necessario: boolean
  ordine: number
}

export interface ListinoAccordoApplicabile {
  id: string
  tipo: 'a_chiamata' | 'programmato'
  valido_dal: string | null
  valido_al: string | null
  voci: ListinoAccordoVoce[]
}

export interface InterventoListinoAccordo {
  accordo_quadro_id: string
  accordo_titolo: string
  accordo_regione: string | null
  data_inizio: string | null
  data_fine: string | null
  fonte: 'esplicito' | 'regione' | 'fallback'
  listini: ListinoAccordoApplicabile[]
}

export interface InterventoSimile {
  id: string
  codice: string
  data_apertura: string
  data_chiusura: string | null
  tipologia: string
  descrizione: string | null
  stato: string
  importo_fatturazione: number | null
  cliente_ragione_sociale: string | null
}

export interface VerificaDoppiInterventi {
  intervento_id: string
  finestra_giorni: number
  interventi_simili: InterventoSimile[]
  n_simili: number
}

export const validazioneApi = {
  getListinoAccordo: async (interventoId: string) => {
    const res = await client.get<InterventoListinoAccordo>(
      `/interventi/${interventoId}/listino-accordo`,
    )
    return res.data
  },
  getDoppiInterventi: async (interventoId: string, finestra_giorni = 365) => {
    const res = await client.get<VerificaDoppiInterventi>(
      `/interventi/${interventoId}/doppi-interventi`,
      { params: { finestra_giorni } },
    )
    return res.data
  },
}

export const pianificazioneApi = {
  primaDataLibera: async (terzistaId: string, slot_orario = '09:00'): Promise<{
    proposta: string | null
    interventi_in_quel_giorno?: number
    max_per_giorno?: number
    messaggio?: string
  }> => {
    const res = await client.get(`/terzisti/${terzistaId}/prima-data-libera`, {
      params: { slot_orario },
    })
    return res.data
  },
}

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

export interface ListaInterventiParams {
  stato?: string
  cliente_id?: string
  terzista_id?: string
  impianto_id?: string
  ticket_id?: string
  punto_vendita_id?: string
  urgente?: boolean
  sla_sforato?: boolean
  sla_sospesa?: boolean
  fase_esecuzione?: 'da_avviare' | 'in_lavorazione'
  in_verifica_costo?: boolean
  codice?: string
  tipologia?: string
  data_da?: string
  data_a?: string
  filtro_data_campo?: 'apertura' | 'pianificata'
  vista?: 'attivi' | 'archivio'
  anno_chiusura?: number
  /** JSON {campo: {in|not_in: [valori]}}: vedi FiltroMulti.filtriInParametro */
  filtri?: string
  page?: number
  page_size?: number
  order_by?: string
  order_dir?: 'asc' | 'desc'
  // legacy — rimossi ma mantenuti per compatibilità con chiamate esistenti
  skip?: number
  limit?: number
}

export interface SospendiSlaPayload {
  motivo: 'in_attesa_analisi' | 'in_attesa_preventivo' | 'in_attesa_oda' | 'altro'
  note?: string
  fino?: string
}

export const MOTIVI_SOSPENSIONE_SLA: { value: SospendiSlaPayload['motivo']; label: string }[] = [
  { value: 'in_attesa_analisi', label: 'In attesa di analisi' },
  { value: 'in_attesa_preventivo', label: 'In attesa di preventivo' },
  { value: 'in_attesa_oda', label: "In attesa di ODA" },
  { value: 'altro', label: 'Altro (specificare nella nota)' },
]

export interface CreaInterventoPayload {
  cliente_id: string
  ticket_id?: string
  impianto_id?: string
  punto_vendita_id?: string
  accordo_quadro_id?: string
  tipologia: TipologiaIntervento
  attivita_ids?: string[]
  descrizione?: string
  urgente?: boolean
  note_coordinatore?: string
  data_pianificata?: string
  durata_prevista_minuti?: number
  data_apertura?: string
  richiesta_targa_nominativi?: boolean
  referente_pv_id?: string
  referente_cliente_id?: string
  referente_nuovo?: {
    tipo: 'pv' | 'cliente'
    nome: string
    cognome?: string
    telefono?: string
    email?: string
  }
}

export interface AssegnaPayload {
  terzista_id: string
  terzista_secondario_id?: string
  data_pianificata: string
  durata_prevista_minuti?: number
  note?: string
  conferma_distanza?: boolean
}

export interface DistanzaTerzista {
  distanza_km: number | null
  calcolabile: boolean
  oltre_soglia: boolean
  soglia_km: number
}

export interface TerzistaStoricoImpianto {
  terzista_id: string
  ragione_sociale: string
  ultimo_intervento: string
  num_interventi: number
}

export interface TerzistaVicino {
  terzista_id: string
  ragione_sociale: string
  distanza_km: number
}

export interface TerzistiConsigliati {
  storico_impianto: TerzistaStoricoImpianto[]
  vicini: TerzistaVicino[]
  vicini_calcolabile: boolean
}

// ---------------------------------------------------------------------------
// Intervento
// ---------------------------------------------------------------------------

export const interventiApi = {
  scaricaBollaIngaggio: async (id: string): Promise<Blob> => {
    const res = await client.get(`/interventi/${id}/bolla-ingaggio-pdf`, { responseType: 'blob' })
    return res.data as Blob
  },

  lista: async (params?: ListaInterventiParams) => {
    const res = await client.get('/interventi', { params })
    return res.data as InterventoListResponse
  },

  dettaglio: async (id: string) => {
    const res = await client.get(`/interventi/${id}`)
    return res.data as InterventoDetail
  },

  crea: async (payload: CreaInterventoPayload) => {
    const res = await client.post('/interventi', payload)
    return res.data as Intervento
  },

  aggiorna: async (id: string, payload: Partial<CreaInterventoPayload>) => {
    const res = await client.patch(`/interventi/${id}`, payload)
    return res.data as Intervento
  },

  // Transizioni FSM
  assegna: async (id: string, payload: AssegnaPayload) => {
    const res = await client.post(`/interventi/${id}/assegna`, payload)
    return res.data as Intervento
  },

  // Distanza sede terzista - punto vendita (18.13)
  distanzaTerzista: async (interventoId: string, terzistaId: string) => {
    const res = await client.get(`/interventi/${interventoId}/distanza-terzista/${terzistaId}`)
    return res.data as DistanzaTerzista
  },

  // Terzisti consigliati: storicità sull'impianto + 3 più vicini (18.24)
  terzistiConsigliati: async (interventoId: string) => {
    const res = await client.get(`/interventi/${interventoId}/terzisti-consigliati`)
    return res.data as TerzistiConsigliati
  },

  iniziaLavori: async (id: string) => {
    const res = await client.post(`/interventi/${id}/inizia-lavori`)
    return res.data as Intervento
  },

  segnaLavoriTerminati: async (id: string, note?: string) => {
    const res = await client.post(`/interventi/${id}/segna-lavori-terminati`, null, { params: { note } })
    return res.data as Intervento
  },

  mandaInValidazione: async (id: string) => {
    const res = await client.post(`/interventi/${id}/manda-in-validazione`)
    return res.data as Intervento
  },

  confermaPrezzi: async (id: string, importo_fatturazione: number, costo_terzista?: number | null, durata_ore?: number | null, kg_rifiuti?: number | null, note?: string, problema_risolto: boolean = true, in_garanzia: boolean = false, dettaglio_prezzo_vendita?: { accordo_titolo: string | null; accordo_fonte: string | null; sconto_totale_pct?: number | null; righe: Array<{ descrizione: string; unita_misura: string | null; prezzo_unitario: number; quantita: number; sconto_pct?: number; importo: number }> } | null) => {
    const res = await client.post(`/interventi/${id}/conferma-prezzi`, { importo_fatturazione, costo_terzista, durata_ore, kg_rifiuti, note, problema_risolto, in_garanzia, dettaglio_prezzo_vendita })
    return res.data as Intervento
  },

  // Validazione multipla (18.10) — "approva tutto" dalla tab Fatturazione → Da validare
  validaBatch: async (items: { intervento_id: string; importo_fatturazione: number }[]) => {
    const res = await client.post('/interventi/valida-batch', { items })
    return res.data as { validati: string[]; errori: { intervento_id: string; errore: string }[] }
  },

  annulla: async (id: string, note: string) => {
    const res = await client.post(`/interventi/${id}/annulla`, { note })
    return res.data as Intervento
  },

  riapri: async (id: string, note: string) => {
    const res = await client.post(`/interventi/${id}/riapri`, { note })
    return res.data as Intervento
  },

  elimina: async (id: string): Promise<void> => {
    await client.delete(`/interventi/${id}`)
  },

  // Hard delete intervento + tutti i riferimenti (solo admin)
  hardDelete: async (id: string): Promise<void> => {
    await client.delete(`/interventi/${id}/hard-delete`)
  },

  // Registro note (18.6)
  listaNote: async (id: string): Promise<InterventoNota[]> => {
    const res = await client.get(`/interventi/${id}/note`)
    return res.data
  },

  creaNota: async (id: string, nota: string): Promise<InterventoNota> => {
    const res = await client.post(`/interventi/${id}/note`, { nota })
    return res.data
  },

  aggiornaNota: async (notaId: number, nota: string): Promise<InterventoNota> => {
    const res = await client.patch(`/interventi/note/${notaId}`, { nota })
    return res.data
  },

  eliminaNota: async (notaId: number): Promise<void> => {
    await client.delete(`/interventi/note/${notaId}`)
  },

  // Media
  listaMedia: async (id: string): Promise<MediaFile[]> => {
    const res = await client.get(`/interventi/${id}/media`)
    return res.data
  },

  uploadMedia: async (
    id: string,
    file: File,
    tipo: string,
    descrizione?: string,
    tipo_documento?: string | null,
    terzista_id_riferimento?: string | null,
  ): Promise<MediaFile> => {
    const init = await client.post(`/interventi/${id}/media`, {
      tipo,
      descrizione: descrizione || null,
      filename: file.name,
      tipo_documento: tipo_documento || null,
      terzista_id_riferimento: terzista_id_riferimento || null,
    })
    const { id: mediaId, presigned_url } = init.data
    await axios.put(presigned_url, file, {
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    })
    const confirm = await client.post('/interventi/media/conferma', { media_id: mediaId })
    return confirm.data as MediaFile
  },

  eliminaMedia: async (mediaId: string): Promise<void> => {
    await client.delete(`/interventi/media/${mediaId}`)
  },

  verificaDocumentazioneAi: async (id: string): Promise<{ esito: string; anomalie: string[]; note_ai: string }> => {
    const res = await client.post(`/interventi/${id}/verifica-documentazione-ai`)
    return res.data
  },

  // Rendiconto
  getRendiconto: async (id: string): Promise<Rendiconto | null> => {
    try {
      const res = await client.get(`/interventi/${id}/rendiconto`)
      return res.data as Rendiconto
    } catch (e: any) {
      if (e?.response?.status === 404) return null
      throw e
    }
  },

  inserimentoManualeRendiconto: async (
    id: string,
    payload: { note_terzista?: string; voci: { descrizione: string; unita_misura: string; quantita: number; prezzo_unitario: number; categoria: string; codice_cer?: string | null; voce_listino_match_id?: string | null }[]; importo_approvato?: number | null },
  ): Promise<Rendiconto> => {
    const res = await client.post(`/interventi/${id}/rendiconto/inserimento-manuale`, payload)
    return res.data as Rendiconto
  },

  approvaRendiconto: async (rendicontoId: string, importo_approvato: number): Promise<Rendiconto> => {
    const res = await client.post(`/rendiconti/${rendicontoId}/approva`, { importo_approvato })
    return res.data as Rendiconto
  },

  contestaRendiconto: async (rendicontoId: string, note: string): Promise<Rendiconto> => {
    const res = await client.post(`/rendiconti/${rendicontoId}/contesta`, { note })
    return res.data as Rendiconto
  },

  getRelazione: async (id: string) => {
    const res = await client.get(`/interventi/${id}/relazione`)
    return res.data
  },

  creaRelazione: async (id: string, payload: { note_tecniche?: string; testo_finale?: string }) => {
    const res = await client.post(`/interventi/${id}/relazione`, payload)
    return res.data
  },

  aggiornaRelazione: async (id: string, payload: { note_tecniche?: string; testo_finale?: string; approvata?: boolean }) => {
    const res = await client.patch(`/interventi/${id}/relazione`, payload)
    return res.data
  },

  riapriRelazione: async (id: string) => {
    const res = await client.post(`/interventi/${id}/relazione/riapri`)
    return res.data
  },

  generaRelazioneAi: async (id: string, note_aggiuntive?: string) => {
    const res = await client.post(`/interventi/${id}/relazione/genera-ai`, { note_aggiuntive })
    return res.data
  },

  generaPdfRelazione: async (id: string): Promise<{ pdf_url: string }> => {
    const res = await client.post(`/interventi/${id}/relazione/genera-pdf`)
    return res.data
  },

  inviaFeedback: async (id: string, email: string) => {
    const res = await client.post(`/interventi/${id}/feedback`, { email_destinatario: email })
    return res.data
  },

  /** Richieste di variazione data inviate dai terzisti dall'app, ancora aperte. */
  countRiprogrammazioni: async (): Promise<{ count: number }> => {
    const res = await client.get('/interventi/riprogrammazioni/count')
    return res.data
  },

  gestisciRiprogrammazione: async (id: string, accetta: boolean) => {
    const res = await client.post(`/interventi/${id}/riprogrammazione/gestisci`, { accetta })
    return res.data as Intervento
  },

  /** Sospende il conteggio SLA (badge + solleciti automatici documentazione) — 18.51. */
  sospendiSla: async (id: string, payload: SospendiSlaPayload) => {
    const res = await client.post(`/interventi/${id}/sospendi-sla`, payload)
    return res.data as Intervento
  },

  /** Toglie la sospensione SLA: il conteggio torna automatico. */
  riprendiSla: async (id: string) => {
    const res = await client.post(`/interventi/${id}/riprendi-sla`)
    return res.data as Intervento
  },

  /** Corregge la bolla firmata e rigenera il PDF (firma originale mantenuta). */
  rettificaBolla: async (id: string, payload: Record<string, unknown>) => {
    const res = await client.patch(`/interventi/${id}/bolla`, payload)
    return res.data as Intervento
  },
}
